import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { OrderScriptStoryboards } from "../../components/orders-list/OrderScriptStoryboards";
import type { OrderScriptRow } from "../../types/marketplace.types";
import {
  describePartnerHookSlots,
  describePartnerScriptProduction,
  hasPartnerScriptProduction,
  type PartnerScriptMetaField,
} from "../lib/partner-script-production";
import { PartnerScriptHookList } from "./PartnerScriptHookList";

const META_LABELS: Record<PartnerScriptMetaField["key"], string> = {
  duration: "Duration",
  presenter: "Presenter",
  product: "Product",
  background: "Background",
};

const SCENE_FIELDS = ["seeing", "hearing", "product", "props"] as const;

export function PartnerScriptProduction({
  script,
  title,
}: {
  script: Pick<OrderScriptRow, "production"> &
    Partial<
      Pick<OrderScriptRow, "hookDeliveries" | "preflightUploadId" | "preflightUrl" | "scriptReviewId">
    >;
  title?: string | null;
}) {
  const { t } = useTranslation();
  const view = describePartnerScriptProduction(script);
  const hookSlots = describePartnerHookSlots(script);
  if (!hasPartnerScriptProduction(view)) return null;

  const hasBriefBlock =
    view.adDescription != null || view.concept != null || view.meta.length > 0;

  return (
    <div className="flex flex-col gap-4">
      {hasBriefBlock ? (
        <div className="space-y-3 rounded-sm bg-muted px-4 py-4">
          {view.adDescription ? (
            <section>
              <h3 className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t(
                  "marketplace.partner.workspace.adDescription",
                  "Ad description",
                )}
              </h3>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                {view.adDescription}
              </p>
            </section>
          ) : null}
          {view.concept ? (
            <section>
              <h3 className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("marketplace.partner.workspace.concept", "Concept")}
              </h3>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                {view.concept}
              </p>
            </section>
          ) : null}
          {view.meta.length > 0 ? (
            <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1.5 text-xs">
              {view.meta.map((field) => (
                <div key={field.key} className="contents">
                  <dt className="text-muted-foreground">
                    {t(
                      `marketplace.partner.workspace.${field.key}`,
                      META_LABELS[field.key],
                    )}
                  </dt>
                  <dd className="text-foreground">{field.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
      ) : null}

      {hookSlots.length > 0 ? (
        <PartnerScriptHookList slots={hookSlots} title={title} />
      ) : null}

      {view.scenes.length > 0 ? (
        <section
          aria-labelledby="partner-script-scenes"
          className="flex flex-col gap-2"
        >
          <div className="flex items-baseline justify-between gap-3">
            <h3
              id="partner-script-scenes"
              className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
            >
              {t("marketplace.partner.workspace.scenes", "Scenes")}
            </h3>
            <span className="text-xs tabular-nums text-muted-foreground">
              {t("marketplace.partner.workspace.sceneCount", {
                count: view.scenes.length,
                defaultValue_one: "{{count}} scene",
                defaultValue_other: "{{count}} scenes",
              })}
            </span>
          </div>
          <div className="overflow-x-auto rounded-sm border border-border">
            <Table className="min-w-[720px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-10 px-3">
                    {t("marketplace.partner.workspace.sceneNumber", "#")}
                  </TableHead>
                  <TableHead className="w-28 px-3">
                    {t("marketplace.partner.workspace.scene", "Scene")}
                  </TableHead>
                  {SCENE_FIELDS.map((field) => (
                    <TableHead key={field} className="px-3">
                      {t(
                        `marketplace.partner.workspace.${field}`,
                        field === "seeing"
                          ? "Seeing"
                          : field === "hearing"
                            ? "Hearing"
                            : field === "product"
                              ? "Product"
                              : "Props",
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {view.scenes.map((scene, index) => (
                  <TableRow
                    key={`${scene.sceneNumber}-${scene.sceneName}`}
                    className={index % 2 === 0 ? "bg-muted/40" : "bg-background"}
                  >
                    <TableCell className="px-3 py-3 align-top tabular-nums text-muted-foreground">
                      {scene.sceneNumber}
                    </TableCell>
                    <TableCell className="px-3 py-3 align-top">
                      <span className="inline-block rounded-sm border border-border bg-background px-2 py-0.5 text-[11px] text-foreground">
                        {scene.sceneName}
                      </span>
                    </TableCell>
                    <TableCell className="px-3 py-3 align-top text-sm leading-relaxed whitespace-pre-wrap">
                      {scene.seeing}
                    </TableCell>
                    <TableCell className="px-3 py-3 align-top text-sm leading-relaxed whitespace-pre-wrap">
                      {scene.hearing}
                    </TableCell>
                    <TableCell className="px-3 py-3 align-top text-sm leading-relaxed whitespace-pre-wrap">
                      {scene.product ?? ""}
                    </TableCell>
                    <TableCell className="px-3 py-3 align-top text-sm leading-relaxed whitespace-pre-wrap">
                      {scene.props}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      ) : null}

      {view.fullScript ? (
        <section className="rounded-sm bg-muted px-4 py-3.5">
          <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t("marketplace.partner.workspace.fullScript", "Full script")}
          </h3>
          <p className="whitespace-pre-wrap text-xs leading-relaxed text-foreground">
            {view.fullScript}
          </p>
        </section>
      ) : null}

      {view.rationale ? (
        <section className="rounded-sm bg-muted px-4 py-3.5">
          <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t("marketplace.partner.workspace.rationale", "Why this works")}
          </h3>
          <p className="whitespace-pre-wrap text-xs leading-relaxed text-foreground">
            {view.rationale}
          </p>
        </section>
      ) : null}

      <OrderScriptStoryboards script={script} title={title} />
    </div>
  );
}
